import React, {useMemo, useRef} from "react"

import PropTypes from "prop-types"
import {Field} from "redux-form"

import {required} from "../../services/validation"
import BoxedRadioButton from "../BoxedRadioButton/boxedRadioButton"
import SemanticFormField from "../SemanticFormField/semanticFormField"

import "./style.scss"

export const BOXED_RADIO_BUTTONS_FIELD_VARIANTS = {DEFAULT: "default"}

const BoxedRadioButtonsField = ({
    buttonsProps,
    disabled = false,
    flexDirection = "row",
    formValues,
    htmlLabel,
    isRequired = false,
    name,
    readOnlyValue,
    variant = BOXED_RADIO_BUTTONS_FIELD_VARIANTS.DEFAULT,
    ...props
}) => {
    const radiogroupRef = useRef(null)
    const labelId = useMemo(() => `label-${name}`, [name])

    // Permet d'associer le label au radiogroup
    const labelWithId = useMemo(() => {
        if (!htmlLabel) {
            return null
        }

        if (React.isValidElement(htmlLabel)) {
            return React.cloneElement(htmlLabel, {id: labelId})
        }

        return <label id={labelId}>{htmlLabel}</label>
    }, [htmlLabel, labelId])

    return (
        <div className="boxed-radio-buttons-field">
            {labelWithId}
            <div
                aria-labelledby={labelWithId ? labelId : undefined}
                className={`boxed-radio-buttons flex-${flexDirection}`}
                ref={radiogroupRef}
                role="radiogroup"
            >
                {buttonsProps.map(button => (
                    <Field
                        as={props => (
                            <BoxedRadioButton
                                checked={
                                    readOnlyValue !== undefined
                                        ? readOnlyValue === button.value
                                        : formValues && formValues[name] === button.value
                                }
                                disabled={disabled}
                                id={`${name}-${button.value}`}
                                key={button.key}
                                name={name}
                                onClick={event => props.onChange(event, {value: button.value})}
                            >
                                {button.label}
                            </BoxedRadioButton>
                        )}
                        component={SemanticFormField}
                        id={`${name}-${button.value}`}
                        key={button.key}
                        name={name}
                        pointing="left"
                        validate={isRequired ? required : null}
                        {...props}
                    />
                ))}
            </div>
        </div>
    )
}

BoxedRadioButtonsField.propTypes = {
    buttonsProps: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.node.isRequired,
            value: PropTypes.string.isRequired,
        }),
    ).isRequired,
    disabled: PropTypes.bool,
    flexDirection: PropTypes.oneOf(["row", "column"]),
    formValues: PropTypes.object,
    htmlLabel: PropTypes.node,
    isRequired: PropTypes.bool,
    name: PropTypes.string.isRequired,
    readOnlyValue: PropTypes.string,
    variant: PropTypes.oneOf(Object.values(BOXED_RADIO_BUTTONS_FIELD_VARIANTS)),
}

export default BoxedRadioButtonsField
